import { Component, EventEmitter, HostBinding, Input, OnChanges, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MunilyPaginationButtonsAlign, MunilyPaginationButtonsSize } from './pagination-buttons.types';

@Component({
  selector: 'common-pagination-buttons',
  templateUrl: './pagination-buttons.component.html',
  styleUrls: ['./pagination-buttons.component.scss'],
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'common-pagination-buttons-host',
  },
})
export class PaginationButtonsComponent implements OnChanges {
  @Input() length = 0;
  @Input() pageIndex = 0;
  @Input() pageSize = 10;
  @Input() pageSizeOptions: number[] = [10, 25, 50, 100];
  @Input() showFirstLastButtons = true;
  @Input() disabled = false;
  @Input() ariaLabel = '';
  @Input() size: MunilyPaginationButtonsSize = 'large';
  @Input() align: MunilyPaginationButtonsAlign = 'start';

  /**
   * Muestra el total de registros (Figma: "Registros totales: 000").
   * Nota: `showRangeLabel` se mantiene por compatibilidad y hace lo mismo.
   */
  @Input() showTotalCount = false;

  /** Compatibilidad: antes se llamaba `showRangeLabel`. */
  @Input() showRangeLabel = false;

  /** Muestra el selector de "items por página" de `mat-paginator`. */
  @Input() showPageSizeSelector = false;

  /** Etiqueta para el contador total (Figma: subtitle_1/s1_500). */
  @Input() totalCountLabel = 'Registros totales:';

  /** Etiqueta para "Registro por página" (Figma: subtitle_2/s2_500). */
  @Input() pageSizeLabel = 'Registro por página';

  /**
   * Opt-in: reemplaza el `mat-paginator` (que nunca dibuja números de página) por una tira
   * numerada con elipsis, flechas y la leyenda "Página X de Y". Por defecto `false` para que
   * ningún consumidor existente cambie de aspecto ni de comportamiento.
   */
  @Input() showPageNumbers = false;

  /** Texto ya traducido para la leyenda "Página X de Y" (mismo patrón que `totalCountLabel`). */
  @Input() pageOfLabel = '';

  /** `aria-label` ya traducido de la flecha anterior. */
  @Input() previousPageLabel = 'Página anterior';

  /** `aria-label` ya traducido de la flecha siguiente. */
  @Input() nextPageLabel = 'Página siguiente';

  /**
   * Tira de páginas visible. `null` es una elipsis.
   *
   * Se precalcula en `ngOnChanges` en vez de exponerse como getter: un getter devolvería un array
   * nuevo en cada ciclo de detección de cambios y el `*ngFor` destruiría y recrearía los botones
   * entre el `mousedown` y el `mouseup`, dejando la tira muerta al clic.
   */
  pageItems: Array<number | null> = [];

  @HostBinding('class.common-pagination-buttons-host--align-center')
  get alignCenterClass(): boolean {
    return this.align === 'center';
  }

  public containerClasses: string[] = [];

  @Output() page = new EventEmitter<PageEvent>();

  constructor() {
    this.syncContainerClasses();
    this.syncPageItems();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['size'] || changes['showRangeLabel'] || changes['showTotalCount'] || changes['showPageSizeSelector'] || changes['showPageNumbers']) {
      this.syncContainerClasses();
    }
    if (changes['length'] || changes['pageSize'] || changes['pageIndex'] || changes['showPageNumbers']) {
      this.syncPageItems();
    }
  }

  onPage(ev: PageEvent): void {
    this.page.emit(ev);
  }

  /** Siempre ≥ 1: una lista vacía sigue siendo "Página 1 de 1", nunca "de 0". */
  get totalPages(): number {
    const size = this.pageSize > 0 ? this.pageSize : 1;
    return Math.max(1, Math.ceil(this.length / size));
  }

  get isFirstPage(): boolean {
    return this.pageIndex <= 0;
  }

  get isLastPage(): boolean {
    return this.pageIndex >= this.totalPages - 1;
  }

  isCurrentPage(page: number): boolean {
    return page === this.pageIndex + 1;
  }

  goToPage(page: number): void {
    if (this.disabled) return;
    const nextIndex = page - 1;
    if (nextIndex < 0 || nextIndex > this.totalPages - 1 || nextIndex === this.pageIndex) return;

    // Mismo contrato que emite `mat-paginator`, para que el handler de cada consumidor siga igual.
    this.page.emit({
      previousPageIndex: this.pageIndex,
      pageIndex: nextIndex,
      pageSize: this.pageSize,
      length: this.length,
    });
  }

  onPrevPage(): void {
    this.goToPage(this.pageIndex);
  }

  onNextPage(): void {
    this.goToPage(this.pageIndex + 2);
  }

  /** Las elipsis no tienen identidad propia: se rastrean por posición. */
  trackByPageItem(index: number, item: number | null): string | number {
    return item === null ? `gap-${index}` : item;
  }

  get showTotalCountResolved(): boolean {
    return this.showTotalCount || this.showRangeLabel;
  }

  onPageSizeSelect(value: unknown): void {
    const next = Math.floor(Number(value));
    if (!Number.isFinite(next) || next <= 0) return;
    if (next === this.pageSize) return;

    const ev: PageEvent = {
      previousPageIndex: this.pageIndex,
      pageIndex: 0,
      pageSize: next,
      length: this.length,
    };
    this.page.emit(ev);
  }

  private syncContainerClasses(): void {
    const next: string[] = [];
    if (this.size === 'large') next.push('common-pagination-buttons--large');
    if (this.size === 'medium') next.push('common-pagination-buttons--medium');
    if (this.showTotalCountResolved) next.push('common-pagination-buttons--show-total');
    if (this.showPageSizeSelector) next.push('common-pagination-buttons--show-page-size');
    if (this.showPageNumbers) next.push('common-pagination-buttons--show-page-numbers');
    this.containerClasses = next;
  }

  /**
   * Ventana de páginas: siempre la primera y la última, más un bloque de 3 alrededor de la actual,
   * con elipsis en los huecos — `1 2 3 … 8`, `1 … 4 5 6 … 8`, `1 … 6 7 8`.
   */
  private syncPageItems(): void {
    const total = this.totalPages;
    if (total <= 7) {
      this.pageItems = Array.from({ length: total }, (_, i) => i + 1);
      return;
    }

    const current = this.pageIndex + 1;
    const visible = new Set<number>([1, total]);
    if (current <= 3) {
      visible.add(2).add(3);
    } else if (current >= total - 2) {
      visible.add(total - 2).add(total - 1);
    } else {
      visible.add(current - 1).add(current).add(current + 1);
    }

    const items: Array<number | null> = [];
    let previous = 0;
    for (const page of Array.from(visible).sort((a, b) => a - b)) {
      if (previous && page - previous > 1) items.push(null);
      items.push(page);
      previous = page;
    }
    this.pageItems = items;
  }
}
